// CartStorage manages the global site state for the shopping cart.
// State is managed using localStorage, which is saved across browser sessions.

import {
  COMPANY_SIZES,
  LicenseHolderState,
  LicenseStylesState,
  LicenseTypesState,
  LicenseUsageState,
} from "../models/Licensing";
import {
  FoundryApiError,
  FoundryError,
  FoundryZodParseError,
} from "../foundry_client/errors";

import { PhysicalProductsState } from "../models/PhysicalProducts";
import { z } from "zod";

export const LOCAL_STORAGE_NAMES = {
  // Use a global name so license usage and holder is shared across all font products.
  fontLicenseUsage: "fonts-add-license-usage",
  fontLicenseHolder: "fonts-add-license-holder",
  // Use fontName-specific keys for storing license types and styles for each family.
  fontLicenseTypes: (fontName) => `${fontName.toLowerCase()}-add-license-types`,
  fontAddStyles: (fontName) => `${fontName.toLowerCase()}-add-styles`,
  physicalProducts: "physical-products",
};

export const clearCart = () => {
  // TODO: Less destructive cleanup of local storage
  localStorage.clear();
};

const LicenseUsageSchema = z.object({
  license_usage_on_behalf_of: z.boolean(),
  license_usage_for_education: z.boolean(),
});

const LicenseHolderSchema = z.object({
  license_holder_name: z.string().trim().min(1),
  license_holder_email_address: z.string().email(),
  license_holder_company_size: z.object(
    Object.fromEntries(
      COMPANY_SIZES.map((size) => [size.toString(), z.boolean()])
    )
  ),
  addons_share_with_third_party: z.boolean(),
});

export function getLicenseUsage(): LicenseUsageState | null {
  const rawData = getData("fontLicenseUsage");
  console.log(rawData);
  const { data, errors } = FoundryZodParseError.validate(
    LicenseUsageSchema,
    rawData
  );
  console.log(errors);

  // TODO: error logging
  return data ? data : null;
}

function validateLicenseHolder(rawData: any): FoundryError[] {
  const { errors } = FoundryZodParseError.validate(
    LicenseHolderSchema,
    rawData
  );

  if (
    !rawData ||
    !(rawData instanceof Object) ||
    Object.values(rawData.license_holder_company_size).filter((val) => val)
      .length != 1
  ) {
    errors.push(new FoundryError("company_size: must be selected."));
  }

  return errors;
}

export function getLicenseHolder(): LicenseHolderState | null {
  const rawData = getData("fontLicenseHolder");
  const errors = validateLicenseHolder(rawData);

  if (errors.length > 0) {
    return null;
  }

  console.log({ rawData });

  return rawData;
}

export function getPlatformSelections(
  fontName: string
): LicenseTypesState | null {
  const data = getData("fontLicenseTypes", fontName);

  // TODO: validate data
  return data;
}

export function getFontLicenseTypes(
  familyName: string
): LicenseTypesState | null {
  const data = getData("fontLicenseTypes", familyName);

  // TODO: validate data
  return data;
}

export function getFontStyles(fontName: string): LicenseStylesState | null {
  const data = getData("fontAddStyles", fontName);

  // TODO: validate data and update IDs based on current product names
  return data;
}

export function getPhysicalProducts(): PhysicalProductsState {
  const data = getData("physicalProducts");

  // TODO: validate data and update IDs based on current product names
  return data;
}

export function getDiscountCode(): string | null {
  return localStorage.getItem("discountCode");
}

export function saveLicenseUsage(
  state: LicenseUsageState,
  skipValidate?: boolean
): FoundryError[] {
  const { errors } = FoundryZodParseError.validate(LicenseUsageSchema, state);

  if (skipValidate || errors.length == 0) {
    localStorage.setItem(
      LOCAL_STORAGE_NAMES.fontLicenseUsage,
      JSON.stringify(state)
    );
  }

  return errors;
}

export function saveLicenseHolder(
  state: LicenseHolderState,
  skipValidate?: boolean
): FoundryError[] {
  const errors = validateLicenseHolder(state);

  if (skipValidate || errors.length == 0) {
    localStorage.setItem(
      LOCAL_STORAGE_NAMES.fontLicenseHolder,
      JSON.stringify(state)
    );
  }

  return errors;
}

export function savePlatformSelections(
  fontName: string,
  state: LicenseTypesState
): FoundryError[] {
  if (!state.app && !state.desktop && !state.logo && !state.web) {
    return [new FoundryError("At least one platform must be selected.")];
  }

  localStorage.setItem(
    LOCAL_STORAGE_NAMES.fontLicenseTypes(fontName),
    JSON.stringify(state)
  );

  return [];
}

export function saveFontStyles(fontName: string, state: LicenseStylesState) {
  localStorage.setItem(
    LOCAL_STORAGE_NAMES.fontAddStyles(fontName),
    JSON.stringify(state)
  );
}

export function savePhysicalProducts(physicalProducts: PhysicalProductsState) {
  localStorage.setItem(
    LOCAL_STORAGE_NAMES.physicalProducts,
    JSON.stringify(physicalProducts)
  );
}

export function saveDiscountCode(discountCode: string) {
  localStorage.setItem("discountCode", discountCode);
}

export function removeDiscountCode() {
  localStorage.removeItem("discountCode");
}

// Safely retrieve JSON-encoded data from localStorage.
const getData = (key, ...props) => {
  let storageName = LOCAL_STORAGE_NAMES[key];
  if (typeof storageName === "function") {
    storageName = storageName(...props);
  }

  const item = localStorage.getItem(storageName);
  if (!item) {
    return;
  }

  console.log(item);
  console.log(storageName);
  return JSON.parse(item);
};
